import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  User, 
  Mail, 
  Shield, 
  Calendar,
  LogOut,
  Building2,
  Bell,
  Webhook,
  FileText,
  Loader2,
  Save,
  RefreshCw,
  TestTube,
  CheckCircle,
  XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_africa-ventures/artifacts/mjqy16nm_image.png";

export default function SettingsPage() {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState({
    admin_email: '',
    slack_webhook_url: '',
    custom_webhook_url: '',
    enable_email_notifications: true,
    enable_slack_notifications: false,
    enable_webhook_notifications: false
  });
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  
  // Email templates state
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // Test states
  const [testingSlack, setTestingSlack] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      fetchNotificationSettings();
      fetchEmailTemplates();
    }
  }, [isAdmin]);

  const fetchNotificationSettings = async () => {
    setLoadingNotifications(true);
    try {
      const response = await axios.get(`${API_URL}/api/settings/notifications`, {
        headers: getAuthHeaders()
      });
      setNotificationSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch notification settings:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const saveNotificationSettings = async () => {
    setSavingNotifications(true);
    try {
      await axios.put(`${API_URL}/api/settings/notifications`, notificationSettings, {
        headers: getAuthHeaders()
      });
      toast.success('Notification settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingNotifications(false);
    }
  };

  const fetchEmailTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await axios.get(`${API_URL}/api/settings/email-templates`, {
        headers: getAuthHeaders()
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch email templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleEditTemplate = async (templateType) => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/email-templates/${templateType}`, {
        headers: getAuthHeaders()
      });
      setEditingTemplate(response.data);
      setTemplateDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load template');
    }
  };

  const saveTemplate = async () => {
    if (!editingTemplate) return;
    
    setSavingTemplate(true);
    try {
      await axios.put(
        `${API_URL}/api/settings/email-templates/${editingTemplate.type}`,
        {
          type: editingTemplate.type,
          subject: editingTemplate.subject,
          html_template: editingTemplate.html_template
        },
        { headers: getAuthHeaders() }
      );
      toast.success('Template saved');
      setTemplateDialogOpen(false);
      fetchEmailTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const resetTemplate = async (templateType) => {
    if (!window.confirm('Reset this template to default?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/settings/email-templates/${templateType}`, {
        headers: getAuthHeaders()
      });
      toast.success('Template reset to default');
      fetchEmailTemplates();
    } catch (error) {
      toast.error('Failed to reset template');
    }
  };

  const testSlack = async () => {
    setTestingSlack(true);
    try {
      const response = await axios.post(`${API_URL}/api/settings/notifications/test-slack`, {}, {
        headers: getAuthHeaders()
      });
      if (response.data.status === 'sent') {
        toast.success('Test Slack notification sent');
      } else if (response.data.status === 'skipped') {
        toast.warning('No Slack webhook URL configured');
      } else {
        toast.error('Failed to send test notification');
      }
    } catch (error) {
      toast.error('Failed to send test notification');
    } finally {
      setTestingSlack(false);
    }
  };

  const testWebhook = async () => {
    setTestingWebhook(true);
    try {
      const response = await axios.post(`${API_URL}/api/settings/notifications/test-webhook`, {}, {
        headers: getAuthHeaders()
      });
      if (response.data.status === 'sent') {
        toast.success('Test webhook sent');
      } else if (response.data.status === 'skipped') {
        toast.warning('No webhook URL configured');
      } else {
        toast.error('Failed to send test webhook');
      }
    } catch (error) {
      toast.error('Failed to send test webhook');
    } finally {
      setTestingWebhook(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-700',
      team_member: 'bg-blue-100 text-blue-700',
      lp: 'bg-slate-100 text-slate-700',
    };
    return colors[role] || colors.lp;
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your account and platform settings</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          {isAdmin && <TabsTrigger value="notifications">Notifications</TabsTrigger>}
          {isAdmin && <TabsTrigger value="templates">Email Templates</TabsTrigger>}
          <TabsTrigger value="about">About</TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card data-testid="profile-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Profile</CardTitle>
              <CardDescription>Your account information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 rounded-full bg-[#1E3A5F] flex items-center justify-center text-white text-2xl font-heading">
                  {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900">{user?.name}</h3>
                  <p className="text-slate-500">{user?.email}</p>
                  <Badge className={`mt-2 ${getRoleBadgeColor(user?.role)}`}>
                    {user?.role?.replace('_', ' ')}
                  </Badge>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-100 rounded-lg">
                    <User className="h-4 w-4 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-slate-500">Full Name</p>
                    <p className="text-slate-900">{user?.name}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-100 rounded-lg">
                    <Mail className="h-4 w-4 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-slate-500">Email</p>
                    <p className="text-slate-900">{user?.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-100 rounded-lg">
                    <Shield className="h-4 w-4 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-slate-500">Role</p>
                    <p className="text-slate-900 capitalize">{user?.role?.replace('_', ' ')}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-100 rounded-lg">
                    <Calendar className="h-4 w-4 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-slate-500">Last Login</p>
                    <p className="text-slate-900">{formatDate(user?.last_login)}</p>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-900 font-medium">Sign out of your account</p>
                  <p className="text-sm text-slate-500">You'll need to sign in again to access the platform</p>
                </div>
                <Button 
                  variant="destructive" 
                  onClick={handleLogout}
                  data-testid="logout-btn"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        {isAdmin && (
          <TabsContent value="notifications">
            <Card data-testid="notifications-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  Notification Settings
                </CardTitle>
                <CardDescription>Configure how you receive notifications for new LP inquiries</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {loadingNotifications ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-[#1E3A5F]" />
                  </div>
                ) : (
                  <>
                    {/* Email Notifications */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <Mail className="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">Email Notifications</p>
                            <p className="text-sm text-slate-500">Receive email alerts for new inquiries</p>
                          </div>
                        </div>
                        <Switch
                          checked={notificationSettings.enable_email_notifications}
                          onCheckedChange={(checked) => setNotificationSettings({
                            ...notificationSettings,
                            enable_email_notifications: checked
                          })}
                          data-testid="email-notifications-switch"
                        />
                      </div>
                      
                      {notificationSettings.enable_email_notifications && (
                        <div className="ml-12">
                          <Label htmlFor="admin_email">Admin Email</Label>
                          <Input
                            id="admin_email"
                            type="email"
                            value={notificationSettings.admin_email}
                            onChange={(e) => setNotificationSettings({
                              ...notificationSettings,
                              admin_email: e.target.value
                            })}
                            placeholder="poche@berkshireafrica.com"
                            className="max-w-md"
                            data-testid="admin-email-input"
                          />
                        </div>
                      )}
                    </div>

                    <Separator />

                    {/* Slack Notifications */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-purple-100 rounded-lg">
                            <svg className="h-4 w-4 text-purple-600" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
                            </svg>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">Slack Notifications</p>
                            <p className="text-sm text-slate-500">Send alerts to a Slack channel</p>
                          </div>
                        </div>
                        <Switch
                          checked={notificationSettings.enable_slack_notifications}
                          onCheckedChange={(checked) => setNotificationSettings({
                            ...notificationSettings,
                            enable_slack_notifications: checked
                          })}
                          data-testid="slack-notifications-switch"
                        />
                      </div>
                      
                      {notificationSettings.enable_slack_notifications && (
                        <div className="ml-12 space-y-3">
                          <div>
                            <Label htmlFor="slack_webhook">Slack Webhook URL</Label>
                            <Input
                              id="slack_webhook"
                              type="url"
                              value={notificationSettings.slack_webhook_url}
                              onChange={(e) => setNotificationSettings({
                                ...notificationSettings,
                                slack_webhook_url: e.target.value
                              })}
                              placeholder="https://hooks.slack.com/services/..."
                              className="max-w-lg"
                              data-testid="slack-webhook-input"
                            />
                            <p className="text-xs text-slate-400 mt-1">
                              Get your webhook URL from Slack → Apps → Incoming Webhooks
                            </p>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={testSlack}
                            disabled={testingSlack || !notificationSettings.slack_webhook_url}
                            data-testid="test-slack-btn"
                          >
                            {testingSlack ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <TestTube className="h-4 w-4 mr-2" />}
                            Test Slack
                          </Button>
                        </div>
                      )}
                    </div>

                    <Separator />

                    {/* Custom Webhook */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-emerald-100 rounded-lg">
                            <Webhook className="h-4 w-4 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">Custom Webhook</p>
                            <p className="text-sm text-slate-500">Send inquiry data to any URL</p>
                          </div>
                        </div>
                        <Switch
                          checked={notificationSettings.enable_webhook_notifications}
                          onCheckedChange={(checked) => setNotificationSettings({
                            ...notificationSettings,
                            enable_webhook_notifications: checked
                          })}
                          data-testid="webhook-notifications-switch"
                        />
                      </div>
                      
                      {notificationSettings.enable_webhook_notifications && (
                        <div className="ml-12 space-y-3">
                          <div>
                            <Label htmlFor="custom_webhook">Webhook URL</Label>
                            <Input
                              id="custom_webhook"
                              type="url"
                              value={notificationSettings.custom_webhook_url}
                              onChange={(e) => setNotificationSettings({
                                ...notificationSettings,
                                custom_webhook_url: e.target.value
                              })}
                              placeholder="https://your-service.com/webhook"
                              className="max-w-lg"
                              data-testid="custom-webhook-input"
                            />
                            <p className="text-xs text-slate-400 mt-1">
                              Receives JSON payload with inquiry data on each submission
                            </p>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={testWebhook}
                            disabled={testingWebhook || !notificationSettings.custom_webhook_url}
                            data-testid="test-webhook-btn"
                          >
                            {testingWebhook ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <TestTube className="h-4 w-4 mr-2" />}
                            Test Webhook
                          </Button>
                        </div>
                      )}
                    </div>

                    <Separator />

                    <div className="flex justify-end">
                      <Button 
                        onClick={saveNotificationSettings}
                        disabled={savingNotifications}
                        className="bg-[#1E3A5F] hover:bg-[#152c4a]"
                        data-testid="save-notifications-btn"
                      >
                        {savingNotifications ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                        Save Settings
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Email Templates Tab */}
        {isAdmin && (
          <TabsContent value="templates">
            <Card data-testid="templates-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Email Templates
                </CardTitle>
                <CardDescription>Customize the email notifications sent for LP inquiries</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingTemplates ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-[#1E3A5F]" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {templates.map((template) => (
                      <div 
                        key={template.type}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200"
                        data-testid={`template-${template.type}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-white rounded-lg border border-slate-200">
                            <Mail className="h-5 w-5 text-[#1E3A5F]" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {template.type === 'admin_notification' ? 'Admin Notification' : 'Auto-Responder'}
                            </p>
                            <p className="text-sm text-slate-500">{template.subject}</p>
                            {template.is_default ? (
                              <Badge variant="secondary" className="mt-1">Using Default</Badge>
                            ) : (
                              <Badge className="mt-1 bg-emerald-100 text-emerald-700">Customized</Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleEditTemplate(template.type)}
                            data-testid={`edit-template-${template.type}`}
                          >
                            Edit
                          </Button>
                          {!template.is_default && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => resetTemplate(template.type)}
                              data-testid={`reset-template-${template.type}`}
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* About Tab */}
        <TabsContent value="about">
          <Card data-testid="about-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg">About</CardTitle>
              <CardDescription>Platform information</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <img 
                  src={LOGO_URL} 
                  alt="Berkshire Africa" 
                  className="h-12"
                />
                <div>
                  <h3 className="font-heading font-semibold text-slate-900">Berkshire Africa Inc</h3>
                  <p className="text-sm text-slate-500">Investment Management Platform v1.0</p>
                </div>
              </div>

              <Separator className="my-6" />

              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2 text-slate-600">
                  <Building2 className="h-4 w-4" />
                  <span>Pan-African Investment Holding Company</span>
                </div>
                <p className="text-slate-500">
                  Building Africa's most valuable financial infrastructure platform, enabling inclusive access to financial services at scale.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Edit {editingTemplate?.type === 'admin_notification' ? 'Admin Notification' : 'Auto-Responder'} Template
            </DialogTitle>
            <DialogDescription>
              Customize the email template. Use variables like {editingTemplate?.variables?.join(', ')}
            </DialogDescription>
          </DialogHeader>

          {editingTemplate && (
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="template_subject">Email Subject</Label>
                <Input
                  id="template_subject"
                  value={editingTemplate.subject || ''}
                  onChange={(e) => setEditingTemplate({...editingTemplate, subject: e.target.value})}
                  placeholder="Email subject line"
                  data-testid="template-subject-input"
                />
              </div>

              <div>
                <Label htmlFor="template_html">HTML Template</Label>
                <Textarea
                  id="template_html"
                  value={editingTemplate.html_template || ''}
                  onChange={(e) => setEditingTemplate({...editingTemplate, html_template: e.target.value})}
                  placeholder="<div>Your HTML template here...</div>"
                  rows={15}
                  className="font-mono text-sm"
                  data-testid="template-html-input"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Available variables: {editingTemplate.variables?.join(', ')}
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={saveTemplate}
              disabled={savingTemplate}
              className="bg-[#1E3A5F] hover:bg-[#152c4a]"
              data-testid="save-template-btn"
            >
              {savingTemplate && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
